import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

import {
  COWORKER_SHEET_PATH,
  FLOORPLAN_VIEW,
  RESOURCES_VIEW_QUERY_PARAM,
} from 'floorplan/pages/native-floorplan/const';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { replace } from 'store/router/actions';
import { searchParams as SearchParams } from 'shared/types/search-params';

interface UseCoworkerSheetNavigationValues {
  openSheet: VoidFunction;
  closeSheet: VoidFunction;
  /** The resources page path without the sheet's segment — where the sheet's actions navigate to. */
  basePathname: string;
}

const sheetSuffix = new RegExp(`/${COWORKER_SHEET_PATH}/?$`);

/** Strips the sheet's route segment, so a url built from inside the sheet lands on the page itself. */
export const stripCoworkerSheetPath = (pathname: string): string => pathname.replace(sheetSuffix, '') || '/';

/**
 * The find-a-coworker sheet lives on a nested route (`…/find-coworker`), like the filters sheet: the
 * back button closes it, and a reload lands on the page rather than on a sheet with no context.
 *
 * Opening also switches the page to the floorplan view (§C2) — the sheet's whole point is to point at
 * a desk on the map, so the map should be behind it when it closes.
 */
export const useCoworkerSheetNavigation = (): UseCoworkerSheetNavigationValues => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const queryParams = useSearchParams<SearchParams>();
  const basePathname = useMemo(() => stripCoworkerSheetPath(pathname), [pathname]);

  const openSheet = useCallback(() => {
    const base = basePathname.endsWith('/') ? basePathname : `${basePathname}/`;
    const nextParams = { ...queryParams, [RESOURCES_VIEW_QUERY_PARAM]: FLOORPLAN_VIEW };

    dispatch(replace(`${base}${COWORKER_SHEET_PATH}?${qs.stringify(nextParams)}`));
  }, [dispatch, basePathname, queryParams]);

  const closeSheet = useCallback(() => {
    dispatch(replace(`${basePathname}?${qs.stringify(queryParams)}`));
  }, [dispatch, basePathname, queryParams]);

  return useMemo(() => ({ openSheet, closeSheet, basePathname }), [openSheet, closeSheet, basePathname]);
};
